import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, RotateCcw, Square, Trash2 } from "lucide-react";
import { ApiError } from "@/api/client";
import {
  useDeleteSuite,
  useRefireSuite,
  useRunners,
  useStopSuite,
  useSuite,
} from "@/api/suites";
import type { SuiteItem, SuiteItemStatus } from "@/api/types";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";
import { Counts, StatusChip } from "./SuitesListPage";

const ITEM_STYLE: Record<SuiteItemStatus, string> = {
  queued: "border-border text-muted-foreground",
  running: "border-sky-500/50 text-sky-500",
  idle: "border-emerald-500/50 text-emerald-500",
  failed: "border-red-500/50 text-red-500",
  skipped: "border-amber-500/50 text-amber-500",
  stopped: "border-amber-500/50 text-amber-500",
};

/** "idle" is the engine's word for a finished load; say so plainly here. */
const ITEM_LABEL: Record<SuiteItemStatus, string> = {
  queued: "queued", running: "running", idle: "loaded",
  failed: "failed", skipped: "skipped", stopped: "stopped",
};

function duration(item: SuiteItem): string {
  if (!item.started_at) return "—";
  const end = item.finished_at ? new Date(item.finished_at) : new Date();
  const secs = Math.max(0, Math.round((end.getTime() - new Date(item.started_at).getTime()) / 1000));
  return `${Math.floor(secs / 60)}m${String(secs % 60).padStart(2, "0")}s`;
}

export function SuiteDetailPage() {
  const { suiteId } = useParams();
  const navigate = useNavigate();
  const suite = useSuite(suiteId);
  const runners = useRunners();
  const stop = useStopSuite();
  const refire = useRefireSuite();
  const del = useDeleteSuite();
  const permissions = usePermissions();
  const [error, setError] = useState<string | null>(null);

  const s = suite.data;
  if (!s) {
    return <div className="p-6 text-sm text-muted-foreground">
      {suite.isLoading ? "Loading…" : "Suite not found."}
    </div>;
  }

  const runner = (runners.data ?? []).find((r) => r.id === s.runner_id);
  const live = s.status === "running" || s.status === "pending";
  const failedCount = (s.counts.failed ?? 0) + (s.counts.stopped ?? 0);

  async function act(fn: () => Promise<unknown>) {
    setError(null);
    try { await fn(); } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }

  return (
    <div className="p-6 space-y-5 max-w-6xl">
      <Link to="/suites"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <ArrowLeft size={12} /> Suites
      </Link>

      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-2xl font-semibold text-foreground">{s.name}</h2>
            <StatusChip status={s.status} />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            <span className="font-mono">{runner?.name ?? s.runner_id}</span>
            {s.backend_kind ? ` · ${s.backend_kind}` : ""}
            {" · "}{s.concurrency} at a time
            {s.allow_cloud_mismatch ? " · cross-cloud allowed" : ""}
          </p>
          <div className="mt-1"><Counts suite={s} /></div>
        </div>
        <div className="flex items-center gap-2">
          {live && (
            <Button variant="outline" disabled={!permissions.canMutate || stop.isPending}
              onClick={() => act(() => stop.mutateAsync(s.id))}>
              <Square size={14} /> Stop
            </Button>
          )}
          {!live && failedCount > 0 && (
            <Button variant="outline" disabled={!permissions.canMutate || refire.isPending}
              onClick={() => act(() => refire.mutateAsync({ id: s.id, failedOnly: true }))}>
              <RotateCcw size={14} /> Retry {failedCount} failed
            </Button>
          )}
          {!live && (
            <Button variant="outline" disabled={!permissions.canMutate || refire.isPending}
              onClick={() => act(() => refire.mutateAsync({ id: s.id, failedOnly: false }))}>
              <RotateCcw size={14} /> Re-run all
            </Button>
          )}
          {!live && (
            <Button variant="outline" disabled={!permissions.canMutate}
              onClick={() => {
                if (confirm(`Delete ${s.name}?`)) {
                  act(async () => { await del.mutateAsync(s.id); navigate("/suites"); });
                }
              }}>
              <Trash2 size={14} />
            </Button>
          )}
        </div>
      </header>

      {s.error && (
        <Alert variant="destructive">
          <AlertDescription>
            <b>{s.error.code}</b> — {s.error.message}
          </AlertDescription>
        </Alert>
      )}
      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

      {runner && !runner.is_local && !runner.online && live && (
        <Alert>
          <AlertDescription>
            <b>{runner.name}</b> hasn't checked in recently. Its work stays queued and
            starts when it calls home — nothing is lost, but nothing is moving either.
          </AlertDescription>
        </Alert>
      )}

      <div className="border border-border rounded-lg overflow-hidden bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/40 text-left">
              <th className="px-3 py-2 text-[10.5px] uppercase tracking-wide font-semibold text-muted-foreground">Scenario</th>
              <th className="px-3 py-2 text-[10.5px] uppercase tracking-wide font-semibold text-muted-foreground">Status</th>
              <th className="px-3 py-2 text-[10.5px] uppercase tracking-wide font-semibold text-muted-foreground">Namespace</th>
              <th className="px-3 py-2 text-[10.5px] uppercase tracking-wide font-semibold text-muted-foreground">Alerts</th>
              <th className="px-3 py-2 text-[10.5px] uppercase tracking-wide font-semibold text-muted-foreground">Time</th>
              <th className="px-3 py-2 text-[10.5px] uppercase tracking-wide font-semibold text-muted-foreground">Detail</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {s.items.map((item) => (
              <tr key={item.scenario_id}>
                <td className="px-3 py-2 font-mono text-foreground align-top">
                  {item.scenario_id}
                </td>
                <td className="px-3 py-2 align-top">
                  <span className={cn(
                    "inline-flex items-center h-5 px-2 rounded-full border text-[10px] font-semibold uppercase tracking-wide",
                    ITEM_STYLE[item.status],
                  )}>
                    {ITEM_LABEL[item.status]}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground align-top">
                  {item.log_namespace ?? "—"}
                </td>
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground align-top">
                  {item.alerts_sent === null || item.alerts_sent === undefined
                    ? "—"
                    : `${item.alerts_sent}${item.alerts_failed ? ` / ${item.alerts_failed} failed` : ""}`}
                </td>
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground align-top">
                  {duration(item)}
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground align-top max-w-md">
                  {item.skip_reason && <span>{item.skip_reason}</span>}
                  {item.error && (
                    <span className="text-red-500">
                      <b>{item.error.code}</b> — {item.error.message}
                    </span>
                  )}
                  {!item.skip_reason && !item.error && item.remote_run_id && (
                    <span className="font-mono">run {item.remote_run_id}</span>
                  )}
                  {!item.skip_reason && !item.error && !item.remote_run_id
                    && item.run_id && runner?.is_local && (
                    <Link to={`/runs/${item.run_id}`}
                      className="font-mono text-sky-500 hover:underline">
                      {item.run_id}
                    </Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
